// src/components/common/ShareMenu.jsx
import React, { useMemo, useState } from 'react';
import {
  Share2,
  Copy,
  Check,
  MessageCircle,
  Facebook,
  Twitter,
  Send as SendIcon,
  Mail,
  X
} from 'lucide-react';

const ShareMenu = ({ slug, event, className = '', buttonClassName = '' }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // URL que trae OG tags desde tu backend
  const shareUrl = useMemo(() => {
    const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
    return `${apiUrl}/share/${slug}/events/${event?._id}`;
  }, [slug, event?._id]);

  const title = event?.title || 'Evento';
  const text = event?.description || '¡No te lo pierdas!';

  const handleSystemShare = async () => {
    // Si ya está abierto, lo cerramos
    if (open) {
      setOpen(false);
      return;
    }

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    try {
      if (isMobile && navigator.share) {
        await navigator.share({
          title,
          text,
          url: shareUrl,
        });
      } else {
        setOpen(true);
      }
    } catch (err) {
      setOpen(true);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { }
  };

  const encodedShare = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(`${title} – ${text}`);

  // Configuración de botones estilo "Big Button" como en CreateEvent
  const shareLinks = [
    {
      name: 'WhatsApp',
      href: `https://wa.me/?text=${encodedText}%20${encodedShare}`,
      color: 'bg-[#25D366] hover:bg-[#1dbf57]',
      icon: <MessageCircle size={24} />
    },
    {
      name: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedShare}`,
      color: 'bg-[#1877F2] hover:bg-[#166fe5]',
      icon: <Facebook size={24} />
    },
    {
      name: 'X (Twitter)',
      href: `https://twitter.com/intent/tweet?url=${encodedShare}&text=${encodeURIComponent(title)}`,
      color: 'bg-[#000000] border border-white/20 hover:bg-white/10',
      icon: <Twitter size={24} />
    },
    {
      name: 'Telegram',
      href: `https://t.me/share/url?url=${encodedShare}&text=${encodedText}`,
      color: 'bg-[#0088cc] hover:bg-[#007dbb]',
      icon: <SendIcon size={24} />
    },
    {
      name: 'Email',
      href: `mailto:?subject=${encodeURIComponent('Mirá este evento')}&body=${encodedText}%0A%0A${encodedShare}`,
      color: 'bg-white/10 hover:bg-white/20',
      icon: <Mail size={24} />
    },
  ];

  return (
    <>
      <button
        type="button"
        onClick={handleSystemShare}
        className={buttonClassName || `inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-all backdrop-blur-sm ${className}`}
        aria-label="Compartir"
      >
        <Share2 size={18} />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-6 animate-in fade-in duration-200">
          {/* Close Overlay */}
          <div className="absolute inset-0" onClick={() => setOpen(false)} />

          <div className="bg-glass border border-white/10 rounded-[32px] max-w-md w-full p-8 relative animate-in zoom-in-90 fade-in duration-300 z-10 shadow-2xl text-base font-normal">
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 p-2 bg-white/5 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-all"
            >
              <X size={20} />
            </button>

            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-primary-orange/20 rounded-full flex items-center justify-center mb-6 text-primary-orange">
                <Share2 size={32} />
              </div>

              <h2 className="text-2xl font-black text-white mb-2 uppercase tracking-wide">Compartir Evento</h2>
              <p className="text-white/60 mb-8 font-medium">Hacé llegar tu evento a más personas.</p>

              <div className="w-full space-y-3 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                {shareLinks.map(link => (
                  <a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full flex items-center justify-center gap-3 py-4 rounded-xl text-white font-bold ${link.color} transition-transform hover:scale-[1.02] shadow-lg`}
                    onClick={() => setOpen(false)}
                  >
                    {link.icon}
                    Compartir en {link.name}
                  </a>
                ))}
              </div>

              <div className="w-full mt-3 pt-4 border-t border-white/5">
                <button
                  onClick={handleCopy}
                  className="w-full bg-white/5 border border-white/10 hover:bg-white/10 py-4 rounded-xl text-white/80 font-bold flex items-center justify-center gap-2 transition-all"
                >
                  {copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}
                  {copied ? '¡Enlace Copiado!' : 'Copiar enlace al evento'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareMenu;
